// Centralized UI copy in the five market languages (EN/RU/DE/IT/FR).
// Flower variety NAMES and DESCRIPTIONS live in the database and are
// intentionally language-neutral — proper names are never translated
// (client rule: names stay in English in every language).

export const TRANSLATIONS = {
  en: {
    nav: {
      home: "Home",
      about: "About Us",
      products: "Products",
      blog: "Blog",
      contact: "Contact",
      search: "Search",
      clientAccess: "Client Access",
    },
    hero: {
      eyebrow: "Premium flowers from Ecuador",
      titleLine1: "From the Andes",
      titleLine2: "to the world's most",
      titleLine3: "unforgettable moments.",
      description:
        "Premium Ecuadorian flowers grown with precision, delivered with consistency and designed to inspire.",
      ctaPrimary: "Explore Our Flowers",
      ctaSecondary: "Become a Tessa Partner",
      scroll: "Scroll to discover",
      videoFallbackAlt:
        "Monumental floral installation at a luxury international wedding",
    },
    rose: {
      aside: "As you scroll",
      title1: "Our roses,",
      title2: "perfected in every detail.",
      features: [
        {
          title: "Ecuadorian Grown",
          text: "Optimal climate, rich soil and responsible farming practices.",
        },
        {
          title: "Hand Selected",
          text: "Carefully selected at the perfect stage.",
        },
        {
          title: "Global Distribution",
          text: "Delivered fresh to international markets.",
        },
        {
          title: "Exceptional Vase Life",
          text: "Long-lasting beauty that inspires confidence.",
        },
      ],
    },
    newVarieties: {
      eyebrow: "Fresh from our collection",
      title: "New variety",
      description:
        "A curated selection of distinctive carnations joining TESSA's international portfolio.",
      viewCollection: "View new varieties",
      previous: "Previous varieties",
      next: "Next varieties",
    },
    season: {
      eyebrow: "Season Picks",
      viewCollection: "View Collection",
      idealPara: "Ideal para",
    },
    explore: {
      eyebrow: "Chapter 01 — The Collection",
      title1: "Explore",
      title2: "our flowers.",
      discover: "Discover",
      varieties: "varieties",
      viewComplete: "View complete collection",
      featured: "Featured varieties",
    },
    markets: {
      eyebrow: "Flowers in their element",
      title1: "For every vision.",
      title2: "For every market.",
      seeAll: "See all markets",
    },
    numbers: {
      title1: "Global scale.",
      title2: "Personal attention to every stem.",
    },
    journey: {
      eyebrow: "Our quality journey",
      title1: "From our farms",
      title2: "to the world.",
    },
    certifications: {
      eyebrow: "Certified responsibility",
      title1: "Certified standards,",
      title2: "trusted worldwide.",
      learnMore: "Learn more about our certifications",
      leftTitle: "Quality is a process, not a promise.",
      leftText:
        "Every stem passes through a controlled chain of care — from high-altitude cultivation to cold-chain logistics — ensuring consistency our partners can build their business on.",
    },
    news: {
      eyebrow: "Floral News",
      title1: "Insights, trends",
      title2: "and inspiration.",
      viewArticle: "View Article",
      viewAll: "View all articles",
    },
    instagram: {
      eyebrow: "Follow our journey",
    },
    form: {
      title1: "Start a partnership",
      title2: "that blooms.",
      subtitle:
        "Tell us about your business and our international sales team will craft a supply program around your needs.",
      company: "Company",
      contactName: "Contact name",
      email: "Business email",
      country: "Country",
      businessType: "Type of business",
      businessTypePlaceholder: "Select your business type",
      message: "Message",
      submit: "Start a Partnership",
      sending: "Sending…",
      success:
        "Thank you. Our international sales team will contact you shortly.",
      successTitle: "Request received",
      failTitle: "Something went wrong",
      fail: "We couldn't send your request. Please try again in a moment.",
      businessTypes: [
        "Importer",
        "Wholesaler",
        "Retailer",
        "Floral Designer",
        "Event Company",
        "Hospitality",
        "Other",
      ],
      errors: {
        required: "This field is required",
        email: "Please enter a valid email address",
        message: "Please write at least 10 characters",
      },
    },
    footer: {
      description:
        "Premium Ecuadorian roses and flowers, grown at altitude and delivered to the world's most demanding markets.",
      explore: "Explore",
      company: "Company",
      connect: "Connect",
      clientAccess: "Client Access",
      privacy: "Privacy Policy",
      terms: "Terms & Conditions",
      grown: "Proudly grown in Ecuador.",
      rights: "All rights reserved.",
    },
    chat: {
      button: "Open TESSA chat",
      close: "Close chat",
      title: "TESSA Assistant",
      status: "Digital concierge",
      welcome:
        "Welcome. Tell us what kind of flowers or commercial program you are looking for.",
      placeholder: "Write your message…",
      send: "Send message",
      sending: "Sending",
      integrationPending:
        "The chat interface is ready. Connect the n8n webhook to activate the intelligent agent.",
      error:
        "We could not reach the assistant. Please try again in a moment.",
      powered: "Ready for n8n integration",
    },
    common: {
      backToTop: "Back to top",
      close: "Close",
    },
    actions: {
      info: "Quick info",
      quickView: "Quick view",
      openCollection: "Open collection",
      viewCollection: "Open variety catalog",
      details: "View full details",
      addFav: "Add to favorites",
      removeFav: "Remove from favorites",
      addedFav: "Added to favorites",
      removedFav: "Removed from favorites",
      add: "Add to request",
      addRequest: "Add to order request",
      inRequest: "In request",
      added: "Added to order request",
    },
    popup: {
      namePlaceholder: "Your name (optional)",
      emailPlaceholder: "Your email",
      subscribe: "Subscribe",
      thanksTitle: "Thank you",
      thanksText: "We'll keep you posted with news from our farms.",
    },
    about: {
      eyebrow: "Our story",
      title1: "The road to 30 years,",
      title2: "still blooming.",
      subtitle: "From our founding in the Ecuadorian Andes to the world's most demanding markets — the milestones that made TESSA.",
    },
    pages: {
      backHome: "Back to home",
      loading: "Loading…",
      catalog: {
        eyebrow: "Catalog",
        title: "Our varieties",
        subtitle: "Search by name and combine colors, subcategories and varieties.",
        searchPlaceholder: "Search a variety…",
        color: "Color",
        subcategory: "Subcategory",
        variety: "Variety",
        clearFilters: "Clear filters",
        resultOne: "variety",
        resultMany: "varieties",
        emptyTitle: "No varieties match your selection.",
        emptyText: "Try removing a filter or searching another name.",
      },
      variety: {
        notFound: "Variety not found.",
        browse: "Browse varieties",
        photoSoon: "Photo coming soon",
        tones: "Tones",
        imageViews: "Image views",
        relatedEyebrow: "Related by color",
        relatedTitle: "You may also like",
      },
      blog: {
        title: "Stories & updates",
        empty: "No stories yet.",
        read: "Read",
      },
      partners: {
        eyebrow: "Partners",
        title: "Our collaborators",
        subtitle: "The breeders, distributors and floral studios we grow with.",
        emptyTitle: "Our partner gallery is being curated.",
        emptyText: "Check back soon.",
      },
      order: {
        eyebrow: "Order request",
        title: "Review your selection",
        unitNoticeTitle: "Unit of sale pending:",
        unitNotice: "the catalog does not yet declare how each variety is sold (stems, bunches, boxes…). Your lines are stored with a placeholder that the admin will replace once the real units are confirmed.",
        varieties: "Varieties",
        emptyTitle: "Your request is empty.",
        unit: "Unit:",
        quantity: "Quantity",
        removeLine: "Remove line",
        contact: "Your contact",
        select: "Select…",
        submit: "Send order request",
        noPayments: "No payments are processed. Your request is saved for our commercial team to follow up.",
        errRequired: "Required",
        errEmail: "Enter a valid email",
        errLines: "Add at least one variety",
        errQty: "Quantities must be ≥ 1",
        submitFail: "Could not submit the request",
      },
      confirmation: {
        title: "Thank you.",
        text: "Your order request has been saved. Our international sales team will follow up with the next steps shortly. No payment was processed.",
        reference: "Reference",
      },
      favorites: {
        eyebrow: "Your selection",
        title: "Favorites",
        subtitle: "Saved on this device. Nothing personal is stored here.",
        clearAll: "Clear all",
        cleared: "Favorites cleared",
        emptyTitle: "No favorites yet.",
        emptyText: "Tap the heart on any variety to save it here.",
        remove: "Remove",
      },
    },
  },
  ru: {
    nav: {
      home: "Главная",
      about: "О нас",
      products: "Продукция",
      blog: "Блог",
      contact: "Контакты",
      search: "Поиск",
      clientAccess: "Вход для клиентов",
    },
    hero: {
      eyebrow: "Премиальные цветы из Эквадора",
      titleLine1: "Из Анд",
      titleLine2: "к самым незабываемым",
      titleLine3: "моментам мира.",
      description:
        "Премиальные эквадорские цветы, выращенные с точностью, поставляемые стабильно и созданные вдохновлять.",
      ctaPrimary: "Смотреть коллекцию",
      ctaSecondary: "Стать партнёром TESSA",
      scroll: "Листайте, чтобы узнать больше",
      videoFallbackAlt:
        "Монументальная цветочная композиция на роскошной международной свадьбе",
    },
    rose: {
      aside: "По мере прокрутки",
      title1: "Наши розы,",
      title2: "совершенство в каждой детали.",
      features: [
        {
          title: "Выращены в Эквадоре",
          text: "Идеальный климат, плодородная почва и ответственное земледелие.",
        },
        {
          title: "Ручной отбор",
          text: "Каждый стебель отбирается на оптимальной стадии.",
        },
        {
          title: "Мировые поставки",
          text: "Свежие цветы доставляются на международные рынки.",
        },
        {
          title: "Долгая жизнь в вазе",
          text: "Стойкая красота, которой можно доверять.",
        },
      ],
    },
    newVarieties: {
      eyebrow: "Новинки коллекции",
      title: "Новый сорт",
      description:
        "Отборные выразительные гвоздики, пополняющие международный ассортимент TESSA.",
      viewCollection: "Смотреть новые сорта",
      previous: "Предыдущие сорта",
      next: "Следующие сорта",
    },
    season: {
      eyebrow: "Выбор сезона",
      viewCollection: "Смотреть коллекцию",
      idealPara: "Идеально для",
    },
    explore: {
      eyebrow: "Глава 01 — Коллекция",
      title1: "Откройте",
      title2: "наши цветы.",
      discover: "Подробнее",
      varieties: "сортов",
      viewComplete: "Смотреть всю коллекцию",
      featured: "Избранные сорта",
    },
    markets: {
      eyebrow: "Цветы в своей стихии",
      title1: "Для каждого замысла.",
      title2: "Для каждого рынка.",
      seeAll: "Смотреть все направления",
    },
    numbers: {
      title1: "Мировой масштаб.",
      title2: "Персональное внимание к каждому стеблю.",
    },
    journey: {
      eyebrow: "Путь качества",
      title1: "От наших плантаций",
      title2: "по всему миру.",
    },
    certifications: {
      eyebrow: "Подтверждённая ответственность",
      title1: "Сертифицированные стандарты,",
      title2: "которым доверяют во всём мире.",
      learnMore: "Подробнее о наших сертификатах",
      leftTitle: "Качество — это процесс, а не обещание.",
      leftText:
        "Каждый стебель проходит контролируемую цепочку заботы — от высокогорного выращивания до холодовой логистики — обеспечивая стабильность, на которой партнёры строят свой бизнес.",
    },
    news: {
      eyebrow: "Новости флористики",
      title1: "Идеи, тенденции",
      title2: "и вдохновение.",
      viewArticle: "Читать статью",
      viewAll: "Все статьи",
    },
    instagram: {
      eyebrow: "Следите за нашей историей",
    },
    form: {
      title1: "Начните партнёрство,",
      title2: "которое расцветает.",
      subtitle:
        "Расскажите о вашем бизнесе, и наша международная команда продаж подготовит программу поставок под ваши задачи.",
      company: "Компания",
      contactName: "Контактное лицо",
      email: "Корпоративная почта",
      country: "Страна",
      businessType: "Тип бизнеса",
      businessTypePlaceholder: "Выберите тип бизнеса",
      message: "Сообщение",
      submit: "Начать партнёрство",
      sending: "Отправка…",
      success:
        "Спасибо. Наша международная команда продаж свяжется с вами в ближайшее время.",
      successTitle: "Заявка получена",
      failTitle: "Что-то пошло не так",
      fail: "Не удалось отправить заявку. Попробуйте ещё раз через минуту.",
      businessTypes: [
        "Импортёр",
        "Оптовая компания",
        "Розничная компания",
        "Флорист-дизайнер",
        "Организатор мероприятий",
        "Гостиничный бизнес",
        "Другое",
      ],
      errors: {
        required: "Обязательное поле",
        email: "Введите корректный адрес электронной почты",
        message: "Введите не менее 10 символов",
      },
    },
    footer: {
      description:
        "Премиальные эквадорские розы и цветы, выращенные в высокогорье и поставляемые на самые требовательные рынки мира.",
      explore: "Разделы",
      company: "Компания",
      connect: "Связаться",
      clientAccess: "Вход для клиентов",
      privacy: "Политика конфиденциальности",
      terms: "Условия использования",
      grown: "С гордостью выращено в Эквадоре.",
      rights: "Все права защищены.",
    },
    chat: {
      button: "Открыть чат TESSA",
      close: "Закрыть чат",
      title: "Ассистент TESSA",
      status: "Цифровой консультант",
      welcome:
        "Добро пожаловать. Расскажите, какие цветы или коммерческая программа вас интересуют.",
      placeholder: "Введите сообщение…",
      send: "Отправить сообщение",
      sending: "Отправка",
      integrationPending:
        "Интерфейс чата готов. Подключите webhook n8n, чтобы активировать интеллектуального агента.",
      error:
        "Не удалось связаться с ассистентом. Повторите попытку через несколько секунд.",
      powered: "Готово к интеграции с n8n",
    },
    common: {
      backToTop: "Наверх",
      close: "Закрыть",
    },
    actions: {
      info: "Краткая информация",
      quickView: "Быстрый просмотр",
      openCollection: "Открыть коллекцию",
      viewCollection: "Открыть каталог сортов",
      details: "Подробнее",
      addFav: "В избранное",
      removeFav: "Убрать из избранного",
      addedFav: "Добавлено в избранное",
      removedFav: "Удалено из избранного",
      add: "В заявку",
      addRequest: "В заявку на заказ",
      inRequest: "В заявке",
      added: "Добавлено в заявку",
    },
    popup: {
      namePlaceholder: "Ваше имя (необязательно)",
      emailPlaceholder: "Ваш email",
      subscribe: "Подписаться",
      thanksTitle: "Спасибо",
      thanksText: "Мы будем держать вас в курсе новостей с наших ферм.",
    },
    about: {
      eyebrow: "Наша история",
      title1: "Путь к 30 годам —",
      title2: "и мы всё ещё цветём.",
      subtitle: "От основания в эквадорских Андах до самых требовательных рынков мира — вехи, которые создали TESSA.",
    },
    pages: {
      backHome: "На главную",
      loading: "Загрузка…",
      catalog: {
        eyebrow: "Каталог",
        title: "Наши сорта",
        subtitle: "Ищите по названию и комбинируйте цвета, подкатегории и сорта.",
        searchPlaceholder: "Найти сорт…",
        color: "Цвет",
        subcategory: "Подкатегория",
        variety: "Сорт",
        clearFilters: "Сбросить фильтры",
        resultOne: "сорт",
        resultMany: "сортов",
        emptyTitle: "По вашему запросу ничего не найдено.",
        emptyText: "Попробуйте убрать фильтр или изменить запрос.",
      },
      variety: {
        notFound: "Сорт не найден.",
        browse: "Смотреть сорта",
        photoSoon: "Фото скоро",
        tones: "Оттенки",
        imageViews: "Ракурсы",
        relatedEyebrow: "Похожие по цвету",
        relatedTitle: "Вам может понравиться",
      },
      blog: {
        title: "Истории и новости",
        empty: "Пока нет историй.",
        read: "Читать",
      },
      partners: {
        eyebrow: "Партнёры",
        title: "Наши коллеги",
        subtitle: "Селекционеры, дистрибьюторы и флористические студии, с которыми мы растём.",
        emptyTitle: "Галерея партнёров формируется.",
        emptyText: "Загляните позже.",
      },
      order: {
        eyebrow: "Заявка на заказ",
        title: "Проверьте ваш выбор",
        unitNoticeTitle: "Единица продажи уточняется:",
        unitNotice: "в каталоге пока не указано, как продаётся каждый сорт (стебли, пучки, коробки…). Ваши позиции сохранены с заполнителем, который администратор заменит после подтверждения реальных единиц.",
        varieties: "Сорта",
        emptyTitle: "Ваша заявка пуста.",
        unit: "Единица:",
        quantity: "Количество",
        removeLine: "Удалить позицию",
        contact: "Ваши контакты",
        select: "Выберите…",
        submit: "Отправить заявку",
        noPayments: "Оплата не требуется. Ваша заявка сохранена, наша коммерческая команда свяжется с вами.",
        errRequired: "Обязательное поле",
        errEmail: "Введите корректный email",
        errLines: "Добавьте хотя бы один сорт",
        errQty: "Количество должно быть ≥ 1",
        submitFail: "Не удалось отправить заявку",
      },
      confirmation: {
        title: "Спасибо.",
        text: "Ваша заявка сохранена. Наша международная команда продаж свяжется с вами в ближайшее время. Оплата не производилась.",
        reference: "Номер заявки",
      },
      favorites: {
        eyebrow: "Ваш выбор",
        title: "Избранное",
        subtitle: "Сохранено на этом устройстве. Никакие личные данные здесь не хранятся.",
        clearAll: "Очистить всё",
        cleared: "Избранное очищено",
        emptyTitle: "Пока нет избранного.",
        emptyText: "Нажмите на сердечко у любого сорта, чтобы сохранить его здесь.",
        remove: "Удалить",
      },
    },
  },
  de: {
    nav: {
      home: "Start",
      about: "Über uns",
      products: "Produkte",
      blog: "Blog",
      contact: "Kontakt",
      search: "Suche",
      clientAccess: "Kundenbereich",
    },
    hero: {
      eyebrow: "Premiumblumen aus Ecuador",
      titleLine1: "Von den Anden",
      titleLine2: "zu den unvergesslichsten",
      titleLine3: "Momenten der Welt.",
      description:
        "Premiumblumen aus Ecuador — mit Präzision angebaut, zuverlässig geliefert und geschaffen, um zu inspirieren.",
      ctaPrimary: "Unsere Blumen entdecken",
      ctaSecondary: "TESSA-Partner werden",
      scroll: "Scrollen zum Entdecken",
      videoFallbackAlt:
        "Monumentale Blumeninstallation auf einer luxuriösen internationalen Hochzeit",
    },
    rose: {
      aside: "Beim Scrollen",
      title1: "Unsere Rosen,",
      title2: "bis ins Detail perfektioniert.",
      features: [
        {
          title: "In Ecuador angebaut",
          text: "Optimales Klima, nährstoffreiche Böden und verantwortungsvoller Anbau.",
        },
        {
          title: "Handverlesen",
          text: "Jeder Stiel wird im perfekten Stadium sorgfältig ausgewählt.",
        },
        {
          title: "Weltweiter Vertrieb",
          text: "Frisch geliefert an internationale Märkte.",
        },
        {
          title: "Außergewöhnliche Vasenhaltbarkeit",
          text: "Langanhaltende Schönheit, die Vertrauen schafft.",
        },
      ],
    },
    newVarieties: {
      eyebrow: "Frisch aus unserer Kollektion",
      title: "Neue Sorte",
      description:
        "Eine kuratierte Auswahl markanter Nelken im internationalen Portfolio von TESSA.",
      viewCollection: "Neue Sorten ansehen",
      previous: "Vorherige Sorten",
      next: "Nächste Sorten",
    },
    season: {
      eyebrow: "Saisonale Auswahl",
      viewCollection: "Kollektion ansehen",
      idealPara: "Ideal für",
    },
    explore: {
      eyebrow: "Kapitel 01 — Die Kollektion",
      title1: "Entdecken Sie",
      title2: "unsere Blumen.",
      discover: "Entdecken",
      varieties: "Sorten",
      viewComplete: "Gesamte Kollektion ansehen",
      featured: "Ausgewählte Sorten",
    },
    markets: {
      eyebrow: "Blumen in ihrem Element",
      title1: "Für jede Vision.",
      title2: "Für jeden Markt.",
      seeAll: "Alle Märkte ansehen",
    },
    numbers: {
      title1: "Globale Größe.",
      title2: "Persönliche Aufmerksamkeit für jeden Stiel.",
    },
    journey: {
      eyebrow: "Unsere Qualitätsreise",
      title1: "Von unseren Farmen",
      title2: "in die Welt.",
    },
    certifications: {
      eyebrow: "Zertifizierte Verantwortung",
      title1: "Zertifizierte Standards,",
      title2: "weltweit geschätzt.",
      learnMore: "Mehr über unsere Zertifizierungen",
      leftTitle: "Qualität ist ein Prozess, kein Versprechen.",
      leftText:
        "Jeder Stiel durchläuft eine kontrollierte Sorgfaltskette — vom Hochlandanbau bis zur Kühlkettenlogistik — für eine Beständigkeit, auf die unsere Partner ihr Geschäft aufbauen können.",
    },
    news: {
      eyebrow: "Floral News",
      title1: "Einblicke, Trends",
      title2: "und Inspiration.",
      viewArticle: "Artikel lesen",
      viewAll: "Alle Artikel",
    },
    instagram: {
      eyebrow: "Begleiten Sie unsere Reise",
    },
    form: {
      title1: "Beginnen Sie eine Partnerschaft,",
      title2: "die erblüht.",
      subtitle:
        "Erzählen Sie uns von Ihrem Unternehmen — unser internationales Verkaufsteam erstellt ein Lieferprogramm nach Ihren Bedürfnissen.",
      company: "Unternehmen",
      contactName: "Ansprechpartner",
      email: "Geschäftliche E-Mail",
      country: "Land",
      businessType: "Art des Unternehmens",
      businessTypePlaceholder: "Wählen Sie Ihre Unternehmensart",
      message: "Nachricht",
      submit: "Partnerschaft beginnen",
      sending: "Wird gesendet…",
      success:
        "Vielen Dank. Unser internationales Verkaufsteam meldet sich in Kürze bei Ihnen.",
      successTitle: "Anfrage erhalten",
      failTitle: "Etwas ist schiefgelaufen",
      fail: "Ihre Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es gleich erneut.",
      businessTypes: [
        "Importeur",
        "Großhändler",
        "Einzelhändler",
        "Florist-Designer",
        "Eventagentur",
        "Hotellerie",
        "Sonstiges",
      ],
      errors: {
        required: "Dieses Feld ist erforderlich",
        email: "Bitte geben Sie eine gültige E-Mail-Adresse ein",
        message: "Bitte schreiben Sie mindestens 10 Zeichen",
      },
    },
    footer: {
      description:
        "Premium-Rosen und Blumen aus Ecuador, im Hochland angebaut und an die anspruchsvollsten Märkte der Welt geliefert.",
      explore: "Entdecken",
      company: "Unternehmen",
      connect: "Kontakt",
      clientAccess: "Kundenbereich",
      privacy: "Datenschutz",
      terms: "Allgemeine Geschäftsbedingungen",
      grown: "Stolz angebaut in Ecuador.",
      rights: "Alle Rechte vorbehalten.",
    },
    chat: {
      button: "TESSA-Chat öffnen",
      close: "Chat schließen",
      title: "TESSA-Assistent",
      status: "Digitaler Concierge",
      welcome:
        "Willkommen. Sagen Sie uns, welche Blumen oder welches Handelsprogramm Sie suchen.",
      placeholder: "Ihre Nachricht…",
      send: "Nachricht senden",
      sending: "Wird gesendet",
      integrationPending:
        "Die Chat-Oberfläche ist bereit. Verbinden Sie den n8n-Webhook, um den intelligenten Assistenten zu aktivieren.",
      error:
        "Der Assistent ist derzeit nicht erreichbar. Bitte versuchen Sie es gleich erneut.",
      powered: "Bereit für die n8n-Integration",
    },
    common: {
      backToTop: "Nach oben",
      close: "Schließen",
    },
    actions: {
      info: "Kurzinfo",
      quickView: "Schnellansicht",
      openCollection: "Kollektion öffnen",
      viewCollection: "Sortenkatalog öffnen",
      details: "Alle Details",
      addFav: "Zu Favoriten",
      removeFav: "Aus Favoriten entfernen",
      addedFav: "Zu Favoriten hinzugefügt",
      removedFav: "Aus Favoriten entfernt",
      add: "Zur Anfrage",
      addRequest: "Zur Bestellanfrage",
      inRequest: "In der Anfrage",
      added: "Zur Bestellanfrage hinzugefügt",
    },
    popup: {
      namePlaceholder: "Ihr Name (optional)",
      emailPlaceholder: "Ihre E-Mail",
      subscribe: "Abonnieren",
      thanksTitle: "Vielen Dank",
      thanksText: "Wir halten Sie mit Neuigkeiten von unseren Farmen auf dem Laufenden.",
    },
    about: {
      eyebrow: "Unsere Geschichte",
      title1: "Auf dem Weg zu 30 Jahren —",
      title2: "immer noch in Blüte.",
      subtitle: "Von unserer Gründung in den ecuadorianischen Anden bis zu den anspruchsvollsten Märkten der Welt — die Meilensteine, die TESSA geprägt haben.",
    },
    pages: {
      backHome: "Zur Startseite",
      loading: "Wird geladen…",
      catalog: {
        eyebrow: "Katalog",
        title: "Unsere Sorten",
        subtitle: "Suchen Sie nach Namen und kombinieren Sie Farben, Unterkategorien und Sorten.",
        searchPlaceholder: "Sorte suchen…",
        color: "Farbe",
        subcategory: "Unterkategorie",
        variety: "Sorte",
        clearFilters: "Filter zurücksetzen",
        resultOne: "Sorte",
        resultMany: "Sorten",
        emptyTitle: "Keine Sorten entsprechen Ihrer Auswahl.",
        emptyText: "Entfernen Sie einen Filter oder suchen Sie einen anderen Namen.",
      },
      variety: {
        notFound: "Sorte nicht gefunden.",
        browse: "Sorten durchsuchen",
        photoSoon: "Foto folgt in Kürze",
        tones: "Farbnuancen",
        imageViews: "Bildansichten",
        relatedEyebrow: "Ähnliche Farben",
        relatedTitle: "Das könnte Ihnen gefallen",
      },
      blog: {
        title: "Geschichten & Neuigkeiten",
        empty: "Noch keine Beiträge.",
        read: "Lesen",
      },
      partners: {
        eyebrow: "Partner",
        title: "Unsere Partner",
        subtitle: "Die Züchter, Händler und Blumenstudios, mit denen wir wachsen.",
        emptyTitle: "Unsere Partnergalerie wird gerade zusammengestellt.",
        emptyText: "Schauen Sie bald wieder vorbei.",
      },
      order: {
        eyebrow: "Bestellanfrage",
        title: "Überprüfen Sie Ihre Auswahl",
        unitNoticeTitle: "Verkaufseinheit ausstehend:",
        unitNotice: "der Katalog gibt noch nicht an, wie jede Sorte verkauft wird (Stiele, Bunde, Kartons…). Ihre Positionen werden mit einem Platzhalter gespeichert, den der Administrator ersetzt, sobald die echten Einheiten feststehen.",
        varieties: "Sorten",
        emptyTitle: "Ihre Anfrage ist leer.",
        unit: "Einheit:",
        quantity: "Menge",
        removeLine: "Position entfernen",
        contact: "Ihr Kontakt",
        select: "Auswählen…",
        submit: "Bestellanfrage senden",
        noPayments: "Es werden keine Zahlungen verarbeitet. Ihre Anfrage wird gespeichert und unser Vertriebsteam meldet sich.",
        errRequired: "Erforderlich",
        errEmail: "Geben Sie eine gültige E-Mail ein",
        errLines: "Fügen Sie mindestens eine Sorte hinzu",
        errQty: "Mengen müssen ≥ 1 sein",
        submitFail: "Die Anfrage konnte nicht gesendet werden",
      },
      confirmation: {
        title: "Vielen Dank.",
        text: "Ihre Bestellanfrage wurde gespeichert. Unser internationales Verkaufsteam meldet sich in Kürze mit den nächsten Schritten. Es wurde keine Zahlung verarbeitet.",
        reference: "Referenz",
      },
      favorites: {
        eyebrow: "Ihre Auswahl",
        title: "Favoriten",
        subtitle: "Auf diesem Gerät gespeichert. Es werden keine persönlichen Daten gespeichert.",
        clearAll: "Alle löschen",
        cleared: "Favoriten gelöscht",
        emptyTitle: "Noch keine Favoriten.",
        emptyText: "Tippen Sie bei einer Sorte auf das Herz, um sie hier zu speichern.",
        remove: "Entfernen",
      },
    },
  },
  it: {
    nav: {
      home: "Home",
      about: "Chi siamo",
      products: "Prodotti",
      blog: "Blog",
      contact: "Contatti",
      search: "Cerca",
      clientAccess: "Area clienti",
    },
    hero: {
      eyebrow: "Fiori premium dall'Ecuador",
      titleLine1: "Dalle Ande",
      titleLine2: "ai momenti più",
      titleLine3: "indimenticabili del mondo.",
      description:
        "Fiori premium ecuadoriani coltivati con precisione, consegnati con costanza e creati per ispirare.",
      ctaPrimary: "Esplora i nostri fiori",
      ctaSecondary: "Diventa partner TESSA",
      scroll: "Scorri per scoprire",
      videoFallbackAlt:
        "Installazione floreale monumentale in un matrimonio internazionale di lusso",
    },
    rose: {
      aside: "Mentre scorri",
      title1: "Le nostre rose,",
      title2: "perfette in ogni dettaglio.",
      features: [
        {
          title: "Coltivate in Ecuador",
          text: "Clima ottimale, terreni ricchi e pratiche agricole responsabili.",
        },
        {
          title: "Selezionate a mano",
          text: "Ogni stelo è selezionato con cura nella fase perfetta.",
        },
        {
          title: "Distribuzione globale",
          text: "Consegnati freschi ai mercati internazionali.",
        },
        {
          title: "Eccezionale durata in vaso",
          text: "Bellezza duratura che ispira fiducia.",
        },
      ],
    },
    newVarieties: {
      eyebrow: "Novità dalla nostra collezione",
      title: "Nuova varietà",
      description:
        "Una selezione curata di garofani distintivi che entrano nel portfolio internazionale di TESSA.",
      viewCollection: "Vedi le nuove varietà",
      previous: "Varietà precedenti",
      next: "Varietà successive",
    },
    season: {
      eyebrow: "Scelte di stagione",
      viewCollection: "Vedi la collezione",
      idealPara: "Ideale per",
    },
    explore: {
      eyebrow: "Capitolo 01 — La collezione",
      title1: "Esplora",
      title2: "i nostri fiori.",
      discover: "Scopri",
      varieties: "varietà",
      viewComplete: "Vedi la collezione completa",
      featured: "Varietà in evidenza",
    },
    markets: {
      eyebrow: "Fiori nel loro elemento",
      title1: "Per ogni visione.",
      title2: "Per ogni mercato.",
      seeAll: "Vedi tutti i mercati",
    },
    numbers: {
      title1: "Scala globale.",
      title2: "Attenzione personale per ogni stelo.",
    },
    journey: {
      eyebrow: "Il nostro percorso di qualità",
      title1: "Dalle nostre fattorie",
      title2: "al mondo.",
    },
    certifications: {
      eyebrow: "Responsabilità certificata",
      title1: "Standard certificati,",
      title2: "apprezzati in tutto il mondo.",
      learnMore: "Scopri di più sulle nostre certificazioni",
      leftTitle: "La qualità è un processo, non una promessa.",
      leftText:
        "Ogni stelo attraversa una catena di cura controllata — dalla coltivazione in altura alla logistica della catena del freddo — garantendo la costanza su cui i nostri partner costruiscono il loro business.",
    },
    news: {
      eyebrow: "Floral News",
      title1: "Approfondimenti, tendenze",
      title2: "e ispirazione.",
      viewArticle: "Leggi l'articolo",
      viewAll: "Tutti gli articoli",
    },
    instagram: {
      eyebrow: "Segui il nostro viaggio",
    },
    form: {
      title1: "Inizia una partnership",
      title2: "che fiorisce.",
      subtitle:
        "Raccontaci la tua attività: il nostro team commerciale internazionale creerà un programma di fornitura su misura per te.",
      company: "Azienda",
      contactName: "Referente",
      email: "Email aziendale",
      country: "Paese",
      businessType: "Tipo di attività",
      businessTypePlaceholder: "Seleziona il tipo di attività",
      message: "Messaggio",
      submit: "Inizia una partnership",
      sending: "Invio in corso…",
      success:
        "Grazie. Il nostro team commerciale internazionale ti contatterà a breve.",
      successTitle: "Richiesta ricevuta",
      failTitle: "Qualcosa è andato storto",
      fail: "Non è stato possibile inviare la richiesta. Riprova tra poco.",
      businessTypes: [
        "Importatore",
        "Grossista",
        "Dettagliante",
        "Floral designer",
        "Agenzia di eventi",
        "Ospitalità",
        "Altro",
      ],
      errors: {
        required: "Campo obbligatorio",
        email: "Inserisci un indirizzo email valido",
        message: "Scrivi almeno 10 caratteri",
      },
    },
    footer: {
      description:
        "Rose e fiori premium ecuadoriani, coltivati in quota e consegnati ai mercati più esigenti del mondo.",
      explore: "Esplora",
      company: "Azienda",
      connect: "Contatti",
      clientAccess: "Area clienti",
      privacy: "Privacy Policy",
      terms: "Termini e condizioni",
      grown: "Coltivato con orgoglio in Ecuador.",
      rights: "Tutti i diritti riservati.",
    },
    chat: {
      button: "Apri la chat TESSA",
      close: "Chiudi la chat",
      title: "Assistente TESSA",
      status: "Concierge digitale",
      welcome:
        "Benvenuto. Dicci quali fiori o quale programma commerciale stai cercando.",
      placeholder: "Scrivi il tuo messaggio…",
      send: "Invia messaggio",
      sending: "Invio in corso",
      integrationPending:
        "L'interfaccia della chat è pronta. Collega il webhook n8n per attivare l'assistente intelligente.",
      error:
        "Impossibile raggiungere l'assistente. Riprova tra poco.",
      powered: "Pronto per l'integrazione n8n",
    },
    common: {
      backToTop: "Torna su",
      close: "Chiudi",
    },
    actions: {
      info: "Info rapide",
      quickView: "Vista rapida",
      openCollection: "Apri la collezione",
      viewCollection: "Apri il catalogo delle varietà",
      details: "Vedi tutti i dettagli",
      addFav: "Aggiungi ai preferiti",
      removeFav: "Rimuovi dai preferiti",
      addedFav: "Aggiunto ai preferiti",
      removedFav: "Rimosso dai preferiti",
      add: "Aggiungi alla richiesta",
      addRequest: "Aggiungi alla richiesta d'ordine",
      inRequest: "Nella richiesta",
      added: "Aggiunto alla richiesta d'ordine",
    },
    popup: {
      namePlaceholder: "Il tuo nome (facoltativo)",
      emailPlaceholder: "La tua email",
      subscribe: "Iscriviti",
      thanksTitle: "Grazie",
      thanksText: "Ti terremo aggiornato con le novità dalle nostre fattorie.",
    },
    about: {
      eyebrow: "La nostra storia",
      title1: "Verso i 30 anni,",
      title2: "sempre in fiore.",
      subtitle: "Dalla fondazione sulle Ande ecuadoriane ai mercati più esigenti del mondo — le tappe che hanno reso TESSA ciò che è.",
    },
    pages: {
      backHome: "Torna alla home",
      loading: "Caricamento…",
      catalog: {
        eyebrow: "Catalogo",
        title: "Le nostre varietà",
        subtitle: "Cerca per nome e combina colori, sottocategorie e varietà.",
        searchPlaceholder: "Cerca una varietà…",
        color: "Colore",
        subcategory: "Sottocategoria",
        variety: "Varietà",
        clearFilters: "Cancella filtri",
        resultOne: "varietà",
        resultMany: "varietà",
        emptyTitle: "Nessuna varietà corrisponde alla selezione.",
        emptyText: "Prova a rimuovere un filtro o a cercare un altro nome.",
      },
      variety: {
        notFound: "Varietà non trovata.",
        browse: "Sfoglia le varietà",
        photoSoon: "Foto in arrivo",
        tones: "Tonalità",
        imageViews: "Viste immagine",
        relatedEyebrow: "Correlate per colore",
        relatedTitle: "Potrebbe piacerti anche",
      },
      blog: {
        title: "Storie e aggiornamenti",
        empty: "Ancora nessuna storia.",
        read: "Leggi",
      },
      partners: {
        eyebrow: "Partner",
        title: "I nostri collaboratori",
        subtitle: "I breeder, i distributori e gli studi floreali con cui cresciamo.",
        emptyTitle: "La nostra galleria dei partner è in preparazione.",
        emptyText: "Torna presto a trovarci.",
      },
      order: {
        eyebrow: "Richiesta d'ordine",
        title: "Rivedi la tua selezione",
        unitNoticeTitle: "Unità di vendita in attesa:",
        unitNotice: "il catalogo non dichiara ancora come viene venduta ogni varietà (steli, mazzi, scatole…). Le tue righe vengono salvate con un segnaposto che l'amministratore sostituirà una volta confermate le unità reali.",
        varieties: "Varietà",
        emptyTitle: "La tua richiesta è vuota.",
        unit: "Unità:",
        quantity: "Quantità",
        removeLine: "Rimuovi riga",
        contact: "I tuoi contatti",
        select: "Seleziona…",
        submit: "Invia richiesta d'ordine",
        noPayments: "Nessun pagamento viene elaborato. La tua richiesta viene salvata e il nostro team commerciale ti ricontatterà.",
        errRequired: "Obbligatorio",
        errEmail: "Inserisci un'email valida",
        errLines: "Aggiungi almeno una varietà",
        errQty: "Le quantità devono essere ≥ 1",
        submitFail: "Impossibile inviare la richiesta",
      },
      confirmation: {
        title: "Grazie.",
        text: "La tua richiesta d'ordine è stata salvata. Il nostro team commerciale internazionale ti contatterà a breve con i prossimi passi. Nessun pagamento è stato elaborato.",
        reference: "Riferimento",
      },
      favorites: {
        eyebrow: "La tua selezione",
        title: "Preferiti",
        subtitle: "Salvato su questo dispositivo. Non viene memorizzato nulla di personale.",
        clearAll: "Cancella tutto",
        cleared: "Preferiti cancellati",
        emptyTitle: "Ancora nessun preferito.",
        emptyText: "Tocca il cuore su una varietà per salvarla qui.",
        remove: "Rimuovi",
      },
    },
  },
  fr: {
    nav: {
      home: "Accueil",
      about: "À propos",
      products: "Produits",
      blog: "Blog",
      contact: "Contact",
      search: "Rechercher",
      clientAccess: "Espace client",
    },
    hero: {
      eyebrow: "Fleurs premium d'Équateur",
      titleLine1: "Des Andes",
      titleLine2: "aux moments les plus",
      titleLine3: "inoubliables du monde.",
      description:
        "Des fleurs premium d'Équateur cultivées avec précision, livrées avec constance et créées pour inspirer.",
      ctaPrimary: "Découvrir nos fleurs",
      ctaSecondary: "Devenir partenaire TESSA",
      scroll: "Faites défiler pour découvrir",
      videoFallbackAlt:
        "Installation florale monumentale lors d'un mariage international de luxe",
    },
    rose: {
      aside: "Au fil du défilement",
      title1: "Nos roses,",
      title2: "perfectionnées dans chaque détail.",
      features: [
        {
          title: "Cultivées en Équateur",
          text: "Climat optimal, sols riches et pratiques agricoles responsables.",
        },
        {
          title: "Sélectionnées à la main",
          text: "Chaque tige est sélectionnée avec soin au stade parfait.",
        },
        {
          title: "Distribution mondiale",
          text: "Livrées fraîches aux marchés internationaux.",
        },
        {
          title: "Tenue en vase exceptionnelle",
          text: "Une beauté durable qui inspire confiance.",
        },
      ],
    },
    newVarieties: {
      eyebrow: "Nouveautés de notre collection",
      title: "Nouvelle variété",
      description:
        "Une sélection d'œillets distinctifs rejoignant le portefeuille international de TESSA.",
      viewCollection: "Voir les nouvelles variétés",
      previous: "Variétés précédentes",
      next: "Variétés suivantes",
    },
    season: {
      eyebrow: "Sélection de saison",
      viewCollection: "Voir la collection",
      idealPara: "Idéal pour",
    },
    explore: {
      eyebrow: "Chapitre 01 — La collection",
      title1: "Découvrez",
      title2: "nos fleurs.",
      discover: "Découvrir",
      varieties: "variétés",
      viewComplete: "Voir toute la collection",
      featured: "Variétés en vedette",
    },
    markets: {
      eyebrow: "Les fleurs dans leur élément",
      title1: "Pour chaque vision.",
      title2: "Pour chaque marché.",
      seeAll: "Voir tous les marchés",
    },
    numbers: {
      title1: "Une échelle mondiale.",
      title2: "Une attention personnelle pour chaque tige.",
    },
    journey: {
      eyebrow: "Notre parcours qualité",
      title1: "De nos fermes",
      title2: "vers le monde.",
    },
    certifications: {
      eyebrow: "Responsabilité certifiée",
      title1: "Des normes certifiées,",
      title2: "reconnues dans le monde entier.",
      learnMore: "En savoir plus sur nos certifications",
      leftTitle: "La qualité est un processus, pas une promesse.",
      leftText:
        "Chaque tige parcourt une chaîne de soins contrôlée — de la culture en altitude à la logistique du froid — garantissant la constance sur laquelle nos partenaires bâtissent leur activité.",
    },
    news: {
      eyebrow: "Floral News",
      title1: "Idées, tendances",
      title2: "et inspiration.",
      viewArticle: "Lire l'article",
      viewAll: "Tous les articles",
    },
    instagram: {
      eyebrow: "Suivez notre aventure",
    },
    form: {
      title1: "Commencez un partenariat",
      title2: "qui s'épanouit.",
      subtitle:
        "Parlez-nous de votre activité : notre équipe commerciale internationale créera un programme d'approvisionnement adapté à vos besoins.",
      company: "Société",
      contactName: "Contact",
      email: "E-mail professionnel",
      country: "Pays",
      businessType: "Type d'activité",
      businessTypePlaceholder: "Sélectionnez votre type d'activité",
      message: "Message",
      submit: "Commencer un partenariat",
      sending: "Envoi en cours…",
      success:
        "Merci. Notre équipe commerciale internationale vous contactera sous peu.",
      successTitle: "Demande reçue",
      failTitle: "Une erreur est survenue",
      fail: "Impossible d'envoyer votre demande. Veuillez réessayer dans un instant.",
      businessTypes: [
        "Importateur",
        "Grossiste",
        "Détaillant",
        "Fleuriste designer",
        "Société d'événementiel",
        "Hôtellerie",
        "Autre",
      ],
      errors: {
        required: "Ce champ est obligatoire",
        email: "Veuillez saisir une adresse e-mail valide",
        message: "Veuillez écrire au moins 10 caractères",
      },
    },
    footer: {
      description:
        "Roses et fleurs premium d'Équateur, cultivées en altitude et livrées aux marchés les plus exigeants du monde.",
      explore: "Explorer",
      company: "Société",
      connect: "Contact",
      clientAccess: "Espace client",
      privacy: "Politique de confidentialité",
      terms: "Conditions générales",
      grown: "Cultivé avec fierté en Équateur.",
      rights: "Tous droits réservés.",
    },
    chat: {
      button: "Ouvrir le chat TESSA",
      close: "Fermer le chat",
      title: "Assistant TESSA",
      status: "Concierge digital",
      welcome:
        "Bienvenue. Dites-nous quelles fleurs ou quel programme commercial vous recherchez.",
      placeholder: "Écrivez votre message…",
      send: "Envoyer le message",
      sending: "Envoi en cours",
      integrationPending:
        "L'interface du chat est prête. Connectez le webhook n8n pour activer l'assistant intelligent.",
      error:
        "Impossible de joindre l'assistant. Veuillez réessayer dans un instant.",
      powered: "Prêt pour l'intégration n8n",
    },
    common: {
      backToTop: "Haut de page",
      close: "Fermer",
    },
    actions: {
      info: "Infos rapides",
      quickView: "Aperçu rapide",
      openCollection: "Ouvrir la collection",
      viewCollection: "Ouvrir le catalogue des variétés",
      details: "Voir tous les détails",
      addFav: "Ajouter aux favoris",
      removeFav: "Retirer des favoris",
      addedFav: "Ajouté aux favoris",
      removedFav: "Retiré des favoris",
      add: "Ajouter à la demande",
      addRequest: "Ajouter à la demande de commande",
      inRequest: "Dans la demande",
      added: "Ajouté à la demande de commande",
    },
    popup: {
      namePlaceholder: "Votre nom (facultatif)",
      emailPlaceholder: "Votre e-mail",
      subscribe: "S'abonner",
      thanksTitle: "Merci",
      thanksText: "Nous vous tiendrons informé des nouvelles de nos fermes.",
    },
    about: {
      eyebrow: "Notre histoire",
      title1: "En route vers 30 ans,",
      title2: "toujours en fleur.",
      subtitle: "De notre fondation dans les Andes équatoriennes aux marchés les plus exigeants du monde — les étapes qui ont fait TESSA.",
    },
    pages: {
      backHome: "Retour à l'accueil",
      loading: "Chargement…",
      catalog: {
        eyebrow: "Catalogue",
        title: "Nos variétés",
        subtitle: "Recherchez par nom et combinez couleurs, sous-catégories et variétés.",
        searchPlaceholder: "Rechercher une variété…",
        color: "Couleur",
        subcategory: "Sous-catégorie",
        variety: "Variété",
        clearFilters: "Effacer les filtres",
        resultOne: "variété",
        resultMany: "variétés",
        emptyTitle: "Aucune variété ne correspond à votre sélection.",
        emptyText: "Essayez de retirer un filtre ou de rechercher un autre nom.",
      },
      variety: {
        notFound: "Variété introuvable.",
        browse: "Parcourir les variétés",
        photoSoon: "Photo bientôt disponible",
        tones: "Nuances",
        imageViews: "Vues de l'image",
        relatedEyebrow: "Similaires par couleur",
        relatedTitle: "Vous aimerez peut-être aussi",
      },
      blog: {
        title: "Histoires & nouveautés",
        empty: "Pas encore d'articles.",
        read: "Lire",
      },
      partners: {
        eyebrow: "Partenaires",
        title: "Nos collaborateurs",
        subtitle: "Les obtenteurs, distributeurs et studios floraux avec lesquels nous grandissons.",
        emptyTitle: "Notre galerie de partenaires est en cours de préparation.",
        emptyText: "Revenez bientôt.",
      },
      order: {
        eyebrow: "Demande de commande",
        title: "Vérifiez votre sélection",
        unitNoticeTitle: "Unité de vente en attente :",
        unitNotice: "le catalogue n'indique pas encore comment chaque variété est vendue (tiges, bouquets, cartons…). Vos lignes sont enregistrées avec un espace réservé que l'administrateur remplacera une fois les unités réelles confirmées.",
        varieties: "Variétés",
        emptyTitle: "Votre demande est vide.",
        unit: "Unité :",
        quantity: "Quantité",
        removeLine: "Supprimer la ligne",
        contact: "Vos coordonnées",
        select: "Sélectionnez…",
        submit: "Envoyer la demande",
        noPayments: "Aucun paiement n'est traité. Votre demande est enregistrée et notre équipe commerciale vous recontactera.",
        errRequired: "Obligatoire",
        errEmail: "Saisissez un e-mail valide",
        errLines: "Ajoutez au moins une variété",
        errQty: "Les quantités doivent être ≥ 1",
        submitFail: "Impossible d'envoyer la demande",
      },
      confirmation: {
        title: "Merci.",
        text: "Votre demande de commande a été enregistrée. Notre équipe commerciale internationale vous contactera prochainement pour la suite. Aucun paiement n'a été traité.",
        reference: "Référence",
      },
      favorites: {
        eyebrow: "Votre sélection",
        title: "Favoris",
        subtitle: "Enregistré sur cet appareil. Aucune donnée personnelle n'est conservée.",
        clearAll: "Tout effacer",
        cleared: "Favoris effacés",
        emptyTitle: "Pas encore de favoris.",
        emptyText: "Touchez le cœur d'une variété pour l'enregistrer ici.",
        remove: "Supprimer",
      },
    },
  },
};
